import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { toast } from "react-toastify";

// Shared BUSINESS/DATA logic for "Review Forms" (the recipient's own
// assigned-forms list). Access derives entirely from
// form_assignment_users.user_id = req.user.id (see routes/
// formSubmissionRoutes.js's requireAssignedFormsAccess()) — never from a
// role name. Extracted so AdminReviewFormsPage.jsx and
// RoleReviewFormsPage.jsx can each own their OWN page JSX while sharing
// every fetch/filter/navigation handler.
function useRolePrefix() {
  const path = window.location.pathname;
  const match = path.match(/^\/([^/]+)\//);
  return match ? match[1] : "admin";
}

// The actual STATUS DETERMINATION (which category a row falls into) is
// business logic and stays shared; each page renders the returned
// {label, tone} pair with its own badge/pill markup.
export function getReviewStatusInfo(row) {
  if (row.is_overdue) return { label: "Overdue", tone: "danger" };
  // Dynamic Forms visibility audit, §11: a reopened-for-changes row's
  // my_status is already back to IN_PROGRESS (services/formSubmissionService
  // .js's applyReopen() flips it) -- checked BEFORE the generic IN_PROGRESS
  // branch below so it reads as "Needs Changes," not an indistinguishable
  // fresh draft.
  if (row.my_status === "IN_PROGRESS" && row.latest_review_status === "CHANGES_REQUESTED") {
    return { label: "Needs Changes", tone: "warning" };
  }
  if (row.my_status === "ASSIGNED") return { label: "Pending", tone: "secondary" };
  if (row.my_status === "IN_PROGRESS") return { label: "In Progress", tone: "info" };
  if (row.my_status === "SUBMITTED" || row.my_status === "COMPLETED") return { label: "Submitted", tone: "success" };
  return { label: row.my_status, tone: "light" };
}

// The recipient ACTION decision (Start/Continue/View/none) is likewise
// shared business logic, not per-page markup -- AdminReviewFormsPage.jsx
// and RoleReviewFormsPage.jsx both drive their action cell off this single
// helper instead of duplicating the same conditions in two places.
//
// Driven entirely by this row's own form_assignment_users.status
// (`my_status`) and its parent form_assignments.status (`assignment_status`)
// -- never reviewer_id/approver_id, and never a role name/permission check
// (permission for the click itself is enforced by the existing
// assigned_forms:start/save_draft/submit backend guards; assigned_forms:
// view_own already gates reaching this page at all via PermissionRoute).
//
// `assignment_status !== 'ACTIVE'` mirrors formSubmissionService.
// startOrResumeSubmission()'s own identical guard -- listMyAssignments
// intentionally still LISTS a DRAFT/CLOSED/ARCHIVED-parent row (so a
// recipient keeps seeing something that was assigned to them even after
// its parent was closed/archived), but the backend would reject a start/
// resume for it with FORM_ASSIGNMENT_INACTIVE, so the button must never be
// offered for it either.
//
// Overdue is DELIBERATELY never a reason to withhold Start/Continue (bug
// fix, product rule): a due date drives reporting/the OVERDUE badge only
// (see getReviewStatusInfo above) and must never revoke fill access on its
// own -- only CANCELLED or an inactive/out-of-scope assignment does. This
// mirrors formSubmissionService.js's own removal of its FORM_ASSIGNMENT_OVERDUE
// guard in startOrResumeSubmission()/submit() -- never a frontend-only
// relaxation of a still-enforced backend rule, and never the reverse.
export function getRecipientAction(row) {
  if (!row) return { type: "none" };

  if (row.my_status === "CANCELLED" || row.assignment_status === "CANCELLED") {
    return { type: "none", reason: "cancelled" };
  }

  if (row.my_status === "SUBMITTED" || row.my_status === "COMPLETED") {
    return row.latest_submission_id
      ? { type: "view" }
      : { type: "none", reason: "no_submission" };
  }

  if (row.my_status === "ASSIGNED" || row.my_status === "IN_PROGRESS") {
    if (row.assignment_status !== "ACTIVE") {
      return { type: "none", reason: "assignment_inactive" };
    }
    // Dynamic Forms visibility audit, §11: same submission/revision, same
    // "continue" mechanics (POST .../submissions resumes the existing OPEN
    // -- DRAFT/REOPENED -- submission) -- only the label differs, so a
    // reviewer's "Changes Requested" decision is visibly actionable rather
    // than looking like a brand-new draft.
    const needsChanges = row.my_status === "IN_PROGRESS" && row.latest_review_status === "CHANGES_REQUESTED";
    return { type: row.my_status === "IN_PROGRESS" ? "continue" : "start", ...(needsChanges ? { needsChanges: true } : {}) };
  }

  return { type: "none" };
}

// Display text for a "none" action's `reason` -- the readonly/expired
// state called for instead of a silently blank Actions cell.
export const RECIPIENT_ACTION_REASON_LABELS = {
  cancelled: "Cancelled",
  assignment_inactive: "Unavailable",
  no_submission: "—",
};

export const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "in_progress", label: "In Progress" },
  { key: "submitted", label: "Submitted" },
  { key: "overdue", label: "Overdue" },
];

export default function useReviewForms() {
  const navigate = useNavigate();
  const rolePrefix = useRolePrefix();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const [statusTab, setStatusTab] = useState("");
  const [formTypeFilter, setFormTypeFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [search, setSearch] = useState("");
  const [formTypes, setFormTypes] = useState([]);
  const [projects, setProjects] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const res = await api.get("/my-form-assignments", {
        params: {
          status: statusTab || undefined,
          form_type_id: formTypeFilter || undefined,
          project_id: projectFilter || undefined,
          search: search || undefined,
          page: currentPage,
          pageSize,
        },
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(res.data?.meta?.total || 0);
    } catch (err) {
      console.error("Fetch my assigned forms error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch your assigned forms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab, formTypeFilter, projectFilter, search, currentPage]);

  useEffect(() => {
    Promise.all([
      api.get("/form-types", { params: { is_active: 1, pageSize: 200 } }).catch(() => null),
      api.get("/projects", { params: { is_active: 1, pageSize: 200 } }).catch(() => null),
    ]).then(([typeRes, projectRes]) => {
      setFormTypes(Array.isArray(typeRes?.data?.data) ? typeRes.data.data : []);
      setProjects(Array.isArray(projectRes?.data?.data) ? projectRes.data.data : []);
    });
  }, []);

  const totalPages = Math.max(Math.ceil(total / pageSize), 1);

  const hasActiveFilters = !!(formTypeFilter || projectFilter || search);
  const clearFilters = () => {
    setCurrentPage(1);
    setFormTypeFilter("");
    setProjectFilter("");
    setSearch("");
  };

  const startOrContinue = async (row) => {
    setBusyId(row.assignment_user_id);
    try {
      const res = await api.post(`/my-form-assignments/${row.assignment_user_id}/submissions`, {});
      const submissionId = res.data?.data?.id;
      navigate(`/${rolePrefix}/my-assigned-forms/${row.assignment_user_id}/fill/${submissionId}`);
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "FORM_SUBMISSION_LIMIT_REACHED") {
        toast.error(data.message || "This form's submission limit has been reached.");
      } else if (data?.code === "FORM_VERSION_INVALID") {
        toast.error(data.message || "This form's configuration is invalid. Please contact an administrator.");
      } else {
        toast.error(data?.message || "Failed to start this form");
      }
    } finally {
      setBusyId(null);
    }
  };

  const viewSubmitted = (row) => {
    if (!row.latest_submission_id) return;
    navigate(`/${rolePrefix}/form-submissions/${row.latest_submission_id}`);
  };

  return {
    rows, total, loading, busyId,
    statusTab, setStatusTab, formTypeFilter, setFormTypeFilter, projectFilter, setProjectFilter,
    search, setSearch, formTypes, projects,
    currentPage, setCurrentPage, pageSize, totalPages,
    hasActiveFilters, clearFilters,
    startOrContinue, viewSubmitted, fetchAssignments,
  };
}
